'use client';

import { useMemo } from 'react';

interface RichTextRendererProps {
  content: string;
  className?: string;
}

export default function RichTextRenderer({ content, className = '' }: RichTextRendererProps) {
  const decodedContent = useMemo(() => {
    // Client-side HTML entity decoding for content that may have been double-encoded
    const decodeHtml = (html: string) => {
      if (!html) return '';

      // Handle multiple levels of HTML entity encoding (max 3 levels to prevent over-decoding)
      let decoded = html;
      for (let i = 0; i < 3; i++) {
        const temp = document.createElement('textarea');
        temp.innerHTML = decoded;
        const newDecoded = temp.value;
        if (newDecoded === decoded) break; // No more decoding needed
        decoded = newDecoded;
      }

      // Clean up Quill.js specific artifacts
      decoded = decoded
        .replace(/<p><br><\/p>/g, '') // Remove empty paragraphs
        .replace(/<span class="ql-cursor"><\/span>/g, '') // Remove Quill cursor artifacts
        .replace(/\s*<p>\s*<\/p>\s*/g, '') // Remove empty paragraphs with whitespace
        .trim();

      return decoded;
    };

    return decodeHtml(content);
  }, [content]);

  return (
    <div
      className={`text-gray-700 text-lg leading-relaxed font-sans ${className}
                 [&_h1]:text-3xl [&_h1]:font-bold [&_h1]:font-heading [&_h1]:text-[#1B1B1B] [&_h1]:mt-8 [&_h1]:mb-4 [&_h1]:leading-tight
                 [&_h2]:text-2xl [&_h2]:md:text-3xl [&_h2]:font-bold [&_h2]:font-poppins [&_h2]:text-[#1B1B1B] [&_h2]:mt-8 [&_h2]:mb-6 [&_h2]:pb-3 [&_h2]:border-b-2 [&_h2]:border-[#C2361E]/20 [&_h2]:leading-tight
                 [&_h3]:text-xl [&_h3]:font-bold [&_h3]:font-heading [&_h3]:text-[#1B1B1B] [&_h3]:mt-6 [&_h3]:mb-3 [&_h3]:leading-snug
                 [&_h4]:text-lg [&_h4]:font-bold [&_h4]:font-heading [&_h4]:text-[#1B1B1B] [&_h4]:mt-4 [&_h4]:mb-2 [&_h4]:leading-snug
                 [&_h5]:text-base [&_h5]:font-bold [&_h5]:font-heading [&_h5]:text-[#1B1B1B] [&_h5]:mt-4 [&_h5]:mb-2 [&_h5]:leading-normal
                 [&_h6]:text-sm [&_h6]:font-bold [&_h6]:font-heading [&_h6]:text-[#1B1B1B] [&_h6]:mt-4 [&_h6]:mb-2 [&_h6]:leading-normal
                 [&_p]:text-gray-700 [&_p]:leading-relaxed [&_p]:mb-4 [&_p]:text-lg [&_p]:font-sans
                 [&_strong]:text-[#C2361E] [&_strong]:font-semibold [&_strong]:font-heading
                 [&_em]:text-gray-600 [&_em]:italic [&_em]:font-sans
                 [&_b]:text-[#C2361E] [&_b]:font-semibold [&_b]:font-heading
                 [&_i]:text-gray-600 [&_i]:italic [&_i]:font-sans
                 [&_u]:underline [&_u]:text-gray-700 [&_u]:font-sans
                 [&_ul]:list-disc [&_ul]:ml-6 [&_ul]:mb-4 [&_ul]:space-y-2 [&_ul]:font-sans
                 [&_ol]:list-decimal [&_ol]:ml-6 [&_ol]:mb-4 [&_ul]:space-y-2 [&_ol]:font-sans
                 [&_li]:text-gray-700 [&_li]:leading-relaxed [&_li]:mb-1 [&_li]:font-sans
                 [&_blockquote]:border-l-4 [&_blockquote]:border-[#C2361E] [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-gray-600 [&_blockquote]:mb-4 [&_blockquote]:bg-gray-50 [&_blockquote]:py-2 [&_blockquote]:font-sans
                 [&_a]:text-[#C2361E] [&_a]:underline [&_a]:font-medium hover:[&_a]:text-[#a22e18] hover:[&_a]:no-underline [&_a]:font-sans
                 [&_code]:bg-gray-100 [&_code]:px-2 [&_code]:py-1 [&_code]:rounded [&_code]:text-sm [&_code]:font-mono [&_code]:text-gray-800
                 [&_pre]:bg-gray-100 [&_pre]:p-4 [&_pre]:rounded [&_pre]:overflow-x-auto [&_pre]:mb-4 [&_pre]:text-sm [&_pre]:font-mono
                 [&_hr]:border-t [&_hr]:border-gray-300 [&_hr]:my-8 [&_hr]:mx-0 [&_hr]:w-full
                 [&>*:last-child]:mb-0 [&_*]:max-w-none`}
      dangerouslySetInnerHTML={{
        __html: decodedContent || content || '<p style="color: #666; font-style: italic;">Content not available. Please check admin settings.</p>'
      }}
    />
  );
}